import React, { useState } from 'react';

interface CustomerInfo {
  name: string;
  contact: string;
  email: string;
  license: string;
  paymentType: 'advance' | 'after';
}

interface CustomerInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (customerInfo: CustomerInfo) => void;
  carInfo: {
    brand: string;
    model: string;
    plate?: string;
    dailyRate?: number;
  };
  rentalDates: {
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
  };
}

export const CustomerInfoModal: React.FC<CustomerInfoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carInfo,
  rentalDates
}) => {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    contact: '',
    email: '',
    license: '',
    paymentType: 'after' // Default to payment after (current logic)
  });

  const [errors, setErrors] = useState<Partial<CustomerInfo>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<CustomerInfo> = {};

    if (!customerInfo.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    if (!customerInfo.contact.trim()) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^[\d\s\-+()]+$/.test(customerInfo.contact)) {
      newErrors.contact = 'Please enter a valid contact number';
    }

    if (!customerInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!customerInfo.license.trim()) {
      newErrors.license = 'Driver license number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onConfirm(customerInfo);
      // Reset form
      setCustomerInfo({
        name: '',
        contact: '',
        email: '',
        license: '',
        paymentType: 'after'
      });
      setErrors({});
    }
  };

  const handleCancel = () => {
    // Reset form
    setCustomerInfo({
      name: '',
      contact: '',
      email: '',
      license: '',
      paymentType: 'after'
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const totalDays = Math.ceil((rentalDates.endDate.getTime() - rentalDates.startDate.getTime()) / (1000 * 60 * 60 * 24)) || 1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-white rounded-t-2xl">
          <h2 className="text-xl font-semibold">Customer Information</h2>
          <p className="text-primary-100 text-sm mt-1">
            Complete rental for {carInfo.brand} {carInfo.model} {carInfo.plate && `(${carInfo.plate})`}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Rental Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Rental Summary</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>From: {rentalDates.startDate.toLocaleDateString()} at {rentalDates.startTime}</p>
              <p>To: {rentalDates.endDate.toLocaleDateString()} at {rentalDates.endTime}</p>
              <p className="font-medium text-primary-600">Duration: {totalDays} day{totalDays === 1 ? '' : 's'}</p>
            </div>
          </div>

          {/* Customer Form */}
          <div className="space-y-4">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter customer's full name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Number *
              </label>
              <input
                type="tel"
                value={customerInfo.contact}
                onChange={(e) => setCustomerInfo({ ...customerInfo, contact: e.target.value })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.contact ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter phone number"
              />
              {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter email address"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Driver License */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver License Number *
              </label>
              <input
                type="text"
                value={customerInfo.license}
                onChange={(e) => setCustomerInfo({ ...customerInfo, license: e.target.value })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.license ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter driver license number"
              />
              {errors.license && <p className="text-red-500 text-xs mt-1">{errors.license}</p>}
            </div>

            {/* Payment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Type *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setCustomerInfo({ ...customerInfo, paymentType: 'advance' })}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    customerInfo.paymentType === 'advance'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">💰</div>
                    <div className="font-semibold">Payment in Advance</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Money goes to fund immediately
                    </div>
                    {carInfo.dailyRate && (
                      <div className="text-sm font-medium text-primary-600 mt-2">
                        {(carInfo.dailyRate * totalDays).toLocaleString('fr-DZ')} DA
                      </div>
                    )}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setCustomerInfo({ ...customerInfo, paymentType: 'after' })}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    customerInfo.paymentType === 'after'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">🔄</div>
                    <div className="font-semibold">Payment After</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Pay on return with charges
                    </div>
                    <div className="text-sm font-medium text-gray-600 mt-2">
                      Current method
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Important</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Please verify customer identification and driver license before confirming the rental.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t rounded-b-2xl">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Confirm Rental
          </button>
        </div>
      </div>
    </div>
  );
};
